import * as Mui from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { SyntheticEvent, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ContextOperation } from "../../../context/OperationContext";
import useTenant from "../../../hooks/useTenant";
import OperationTypes from "../../../types";
import AcrissType from "../../../types/AcrissType";
import CarClassType from "../../../types/CarClassType";
import CarSegmentType from "../../../types/CarSegmentType";
import { availabilityReportActions } from "../service/availabilityReport.actions";

/**
 * Mapping codice ISO country (come restituito dal BFF nel gruppo GroupType.NATION,
 * es. "IT") → label nazione in lingua madre (RF-02 CR LPP-20500).
 * Il backend aggrega le location per `country` (codice ISO) e crea un gruppo
 * NATION con displayName = codice ISO; il FE lo traduce in lingua madre.
 */
const NATION_LABEL_BY_ISO: Record<string, string> = {
  IT: "ITALIA",
  FR: "FRANCE",
  ES: "ESPAÑA",
  PT: "PORTUGAL",
};

/**
 * Vincolo finestra temporale (RF-01 CR LPP-20500): la data AL non può
 * superare 180 giorni dalla data DAL. Il vincolo è sul date picker,
 * impedisce richieste con range superiore senza errori post-submit.
 */
const MAX_DATE_RANGE_DAYS = 180;

/**
 * Vincolo retention (RF-14 CR LPP-20500): la data DAL non può essere più
 * vecchia di 12 mesi dalla data corrente di richiesta. I dati restano in
 * persistenza (nessuna cancellazione), ma la consultazione del report è
 * limitata agli ultimi 12 mesi; oltre tale soglia la consultazione storica
 * avviene su Qlik (RNF-03). Vincolo solo lato frontend sul date picker.
 */
const RETENTION_MONTHS = 12;

/**
 * Opzioni filtro location status (RF-02 CR LPP-20500). Default: ACTIVATED.
 */
type LocationStatusOption = "ACTIVATED" | "TERMINATED" | "ALL";

export interface IAvailabilityFilters {
  /** Date range con vincolo 180 gg (RF-01) */
  startDate?: Dayjs | null;
  endDate?: Dayjs | null;

  /** Filtro location pickup (preesistente) */
  location: any[];

  /** Toggle solo customer at desk (preesistente) */
  onlyCustomer: boolean;

  /** Filtro location status (RF-02), default ACTIVATED */
  locationStatus: LocationStatusOption;

  /** Filtri cascata (RF-04, RF-05) — multi-select */
  carSegmentFilter: CarSegmentType[];
  carClassFilter: CarClassType[];
  acrissFilter: AcrissType[];

  /** Filtro segmento di mercato (RF-07) — multi-select */
  marketSegmentFilter: { id: string; descr: string }[];
}

type Props = {
  filters: IAvailabilityFilters;
  setFilters: (f: IAvailabilityFilters) => void;
  onClickProceed: () => void;
};

export const FilterBox = (props: Props) => {
  const { tenant } = useContext(ContextOperation);

  const { t } = useTranslation(["operation"]);
  const { TenantAutocomplete, resetTenant } = useTenant();

  const { filters, setFilters } = props;

  // Liste sorgente per la cascata (caricate one-shot al cambio tenant).
  const [allLocations, setAllLocations] = useState<any[]>([]);
  const [allCarSegments, setAllCarSegments] = useState<CarSegmentType[]>([]);
  const [allCarClasses, setAllCarClasses] = useState<CarClassType[]>([]);
  const [allAcriss, setAllAcriss] = useState<AcrissType[]>([]);
  const [allMarketSegments, setAllMarketSegments] = useState<{ id: string; descr: string }[]>([]);

  const checkinEstimatedLocationType = useMemo(
    () => [
      { id: "PARKING_GO", descr: t("internOptionSelectName.physicalLocationType.parkingGo") },
      { id: "LMS", descr: t("internOptionSelectName.physicalLocationType.lms") },
      { id: "COMPOUND", descr: t("internOptionSelectName.physicalLocationType.compound") },
      { id: "SHOP_REPAIR", descr: t("internOptionSelectName.physicalLocationType.shopRepair") },
    ],
    [t]
  );

  const getLocationTypeTranslation = (locationType: string) => {
    const tr = checkinEstimatedLocationType.find((i) => i.id === locationType);
    return tr ? tr.descr : locationType;
  };

  // ─── Caricamento liste cascata e segmenti al cambio tenant ──────────────
  useEffect(() => {
    if (tenant === "all" || !tenant) return;
    const actions = availabilityReportActions();
    actions.getCarSegmentList(tenant).then((data) =>
      setAllCarSegments(
        (data || []).slice().sort((a: CarSegmentType, b: CarSegmentType) => (a.descr ?? "").localeCompare(b.descr ?? ""))
      )
    );
    actions.getCarClassList(tenant).then((data) =>
      setAllCarClasses(
        (data || []).slice().sort((a: CarClassType, b: CarClassType) => (a.descr ?? "").localeCompare(b.descr ?? ""))
      )
    );
    actions.getAcrissList(tenant).then((data) => setAllAcriss(data || []));
    actions.getMarketSegmentList().then((data) => {
      // /booking/search/segments restituisce {data: GenericDTO[]} oppure GenericDTO[]
      const list = Array.isArray(data) ? data : data?.data || [];
      setAllMarketSegments(list.map((x: any) => ({ id: x.id || x.code || x, descr: x.descr || x.label || x })));
    });
    getAllLocationGroup(tenant);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant, filters.locationStatus]);

  /**
   * CR LPP-20500 — al cambio dello stato location (es. ACTIVATED → TERMINATED) la lista
   * `allLocations` viene ricaricata: rimuovo dalla selezione i valori che NON fanno più parte
   * della nuova lista (match per id), lasciando quelli ancora presenti.
   */
  useEffect(() => {
    if (filters.location.length === 0) return;
    const validIds = new Set(allLocations.map((l: any) => l.id));
    const pruned = filters.location.filter((loc: any) => validIds.has(loc.id));
    if (pruned.length !== filters.location.length) {
      setFilters({ ...filters, location: pruned });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allLocations]);

  /**
   * CR LPP-20500 — al cambio tenant svuoto completamente la selezione location: la lista
   * caricata si riferisce a un altro tenant e i valori precedenti non sono più validi.
   */
  const previousTenantRef = useRef(tenant);
  useEffect(() => {
    if (previousTenantRef.current !== tenant) {
      previousTenantRef.current = tenant;
      if (filters.location.length > 0) {
        setFilters({ ...filters, location: [] });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant]);

  const getAllLocationGroup = (tenantArg: string) => {
    if (tenantArg === "all") return;
    availabilityReportActions()
      .getAllLocationAngGroup(tenantArg, filters.locationStatus)
      .then((response: any) => {
        const allData = response.map((loc: any) => {
          const currentLoc: any[] = [];
          // CR LPP-20500 RF-02: per i gruppi NATION il displayName dal BFF è il codice
          // ISO (es. "IT"); lo traduciamo in lingua madre (ITALIA/FRANCE/...).
          const isNation = loc.element.groupType === "NATION";
          const groupDisplayName = isNation
            ? (NATION_LABEL_BY_ISO[loc.element.displayName] ?? loc.element.displayName)
            : loc.element.displayName;
          const label = loc.element.locationList
            ? `${groupDisplayName} (${t(`availabilityReport.groupEnums.${loc.element.groupType}`)})`
            : `${loc.element.displayName} (${getLocationTypeTranslation(loc.element.locationType)})`;
          const pureLabel = loc.element.locationList
            ? `${groupDisplayName}`
            : `${loc.element.displayName} (${getLocationTypeTranslation(loc.element.locationType)})`;
          currentLoc.push({
            ...loc,
            label,
            pureLabel,
            locationType: loc.element.locationType,
            elementType: loc.elementType,
            id: loc.element.id,
            mainId: loc.element.id,
            isChild: false,
            isParent: Boolean(loc.element.locationList),
            groupType: loc.element.groupType,
          });
          if (loc.element.locationList) {
            loc.element.locationList.forEach((location: any) => {
              currentLoc.push({
                ...location,
                label: `${location.displayName} (${getLocationTypeTranslation(location.locationType)})`,
                locationType: location.locationType,
                elementType: loc.elementType,
                id: location.id,
                mainId: loc.element.id,
                isChild: true,
              });
            });
          }
          return currentLoc;
        });
        setAllLocations(allData.flat());
      })
      .catch((error: any) => console.log("error", error));
  };

  // ─── Cascata RF-05: filtri visualizzati = lista filtrata in base al livello superiore ──
  const filteredCarClasses = useMemo<CarClassType[]>(() => {
    if (filters.carSegmentFilter.length === 0) return allCarClasses;
    const selectedIds = new Set(filters.carSegmentFilter.map((s) => s.id));
    return allCarClasses.filter((c) => c.carSegment && selectedIds.has(c.carSegment.id));
  }, [filters.carSegmentFilter, allCarClasses]);

  const filteredAcriss = useMemo<AcrissType[]>(() => {
    if (filters.carClassFilter.length === 0 && filters.carSegmentFilter.length === 0) return allAcriss;
    const allowedClassIds = new Set(
      filters.carClassFilter.length > 0
        ? filters.carClassFilter.map((c) => c.id)
        : filteredCarClasses.map((c) => c.id)
    );
    return allAcriss.filter((a) => a.carClass && allowedClassIds.has(a.carClass.id));
  }, [filters.carClassFilter, filters.carSegmentFilter, allAcriss, filteredCarClasses]);

  // Quando l'utente seleziona un car segment, rimuovi le car class non più
  // coerenti col nuovo set di segment (cascata invariante).
  useEffect(() => {
    const allowedIds = new Set(
      filters.carSegmentFilter.length > 0
        ? filters.carSegmentFilter.map((s) => s.id)
        : []
    );
    if (allowedIds.size === 0) return;
    const purgedClasses = filters.carClassFilter.filter(
      (c) => c.carSegment && allowedIds.has(c.carSegment.id)
    );
    if (purgedClasses.length !== filters.carClassFilter.length) {
      setFilters({ ...filters, carClassFilter: purgedClasses });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.carSegmentFilter]);

  useEffect(() => {
    // Stessa logica per ACRISS rispetto alle car class.
    const allowedIds = new Set(
      filters.carClassFilter.length > 0
        ? filters.carClassFilter.map((c) => c.id)
        : []
    );
    if (allowedIds.size === 0) return;
    const purgedAcriss = filters.acrissFilter.filter(
      (a) => a.carClass && allowedIds.has(a.carClass.id)
    );
    if (purgedAcriss.length !== filters.acrissFilter.length) {
      setFilters({ ...filters, acrissFilter: purgedAcriss });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.carClassFilter]);

  // ─── Vincolo date 180 gg (RF-01) ────────────────────────────────────────
  const maxEndDate = useMemo<Dayjs | undefined>(() => {
    if (!filters.startDate) return undefined;
    return filters.startDate.add(MAX_DATE_RANGE_DAYS, "day");
  }, [filters.startDate]);

  // ─── Vincolo retention 12 mesi (RF-14) ──────────────────────────────────
  // La data DAL non può essere più vecchia di 12 mesi da oggi. Calcolata una
  // sola volta al mount per evitare drift durante la sessione.
  const minStartDate = useMemo<Dayjs>(() => dayjs().subtract(RETENTION_MONTHS, "month"), []);

  // ─── Reset / proceed ────────────────────────────────────────────────────
  const onClickReset = () => {
    resetTenant();
    setFilters({
      startDate: dayjs(),
      endDate: dayjs().add(30, "day"),
      location: [],
      onlyCustomer: true,
      locationStatus: "ACTIVATED",
      carSegmentFilter: [],
      carClassFilter: [],
      acrissFilter: [],
      marketSegmentFilter: [],
    });
  };

  const onFilterOptions = (options: any[], { inputValue }: any) => {
    if (!inputValue) return options;
    const parents = options.filter(
      (o) => !o.isChild && o?.label?.toLowerCase().includes(inputValue.toLowerCase())
    );
    const parentIds = new Set(parents.map((p: any) => p.mainId));
    return options.filter((o: any) => parentIds.has(o.mainId));
  };

  const onChangeLocations = (_e: SyntheticEvent, data: any[]) => {
    setFilters({ ...filters, location: data });
  };

  return (
    <Mui.Paper sx={{ backgroundColor: "white", p: 2 }}>
      <form>
        <Mui.Grid
          container
          spacing={2.5}
          alignItems="center"
          alignContent="center"
          direction="row"
        >
          <Mui.Grid item xs={12}>
            <Mui.Typography className="filtersHeader">
              {t("availabilityReport.settings")}
            </Mui.Typography>
          </Mui.Grid>

          <Mui.Grid item xs={12}>
            <Mui.Grid container columns={12} spacing={2.5}>
              {/* ─── RIGA 1: tenant, date, stato location, location ─── */}
              {TenantAutocomplete}

              {/* RF-01: date DAL/AL con vincolo 180 gg */}
              <Mui.Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label={t("availabilityReport.dateFrom", "Data DAL")}
                  value={filters.startDate ?? null}
                  minDate={minStartDate}
                  onChange={(v) => {
                    let newStart = v as Dayjs | null;
                    // RF-14: clamp del DAL alla soglia di retention (12 mesi).
                    if (newStart && newStart.isBefore(minStartDate, "day")) {
                      newStart = minStartDate;
                    }
                    let newEnd = filters.endDate;
                    if (newStart && newEnd && newEnd.diff(newStart, "day") > MAX_DATE_RANGE_DAYS) {
                      newEnd = newStart.add(MAX_DATE_RANGE_DAYS, "day");
                    }
                    setFilters({ ...filters, startDate: newStart, endDate: newEnd });
                  }}
                  renderInput={(params) => (
                    <Mui.TextField
                      {...params}
                      variant="standard"
                      fullWidth
                      helperText={t(
                        "availabilityReport.retentionHint",
                        "Consultabili gli ultimi 12 mesi. Per dati più vecchi usare Qlik."
                      )}
                    />
                  )}
                />
              </Mui.Grid>
              <Mui.Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label={t("availabilityReport.dateTo", "Data AL")}
                  value={filters.endDate ?? null}
                  minDate={filters.startDate ?? undefined}
                  maxDate={maxEndDate}
                  onChange={(v) => setFilters({ ...filters, endDate: v as Dayjs | null })}
                  renderInput={(params) => (
                    <Mui.TextField {...params} variant="standard" fullWidth />
                  )}
                />
              </Mui.Grid>

              {/* RF-02: filtro nazione — la nazione è ora un GroupType.NATION
                  nella pickup list (lista location qui sotto), non un dropdown
                  separato. Vedi getAllLocationGroup + NATION_LABEL_BY_ISO. */}

              {/* RF-02: location status */}
              <Mui.Grid item xs={12} sm={6} md={2}>
                <Mui.Autocomplete
                  options={["ACTIVATED", "TERMINATED", "ALL"] as LocationStatusOption[]}
                  value={filters.locationStatus}
                  disableClearable
                  onChange={(_, v) =>
                    setFilters({ ...filters, locationStatus: v as LocationStatusOption })
                  }
                  getOptionLabel={(o) =>
                    t(`availabilityReport.locationStatus.${o.toLowerCase()}`, o)
                  }
                  renderInput={(p) => (
                    <Mui.TextField
                      {...p}
                      label={t("availabilityReport.locationStatusLabel", "Location status")}
                      variant="standard"
                      fullWidth
                    />
                  )}
                />
              </Mui.Grid>

              {/* Location pickup (preesistente) — a destra di stato location */}
              <Mui.Grid item xs={12} md={3}>
                <Mui.Autocomplete
                  id="lmsAutocomplete"
                  multiple
                  value={filters.location}
                  options={allLocations}
                  filterOptions={onFilterOptions}
                  getOptionLabel={(o: any) => o?.label ?? ""}
                  onChange={onChangeLocations}
                  getOptionDisabled={(o: any) => o.isChild}
                  renderInput={(p) => (
                    <Mui.TextField
                      {...p}
                      label={t("availabilityReport.lmsLabel")}
                      variant="standard"
                      fullWidth
                    />
                  )}
                  renderOption={(props, option, _state) => (
                    <li
                      {...props}
                      key={`${option.id}-${option.mainId}-${option.locationType}`}
                    >
                      <Mui.Typography
                        sx={{ ...(option.isChild && { paddingLeft: 2 }) }}
                        variant={option.isParent ? "h6" : "body1"}
                      >
                        {option.label}
                      </Mui.Typography>
                    </li>
                  )}
                />
              </Mui.Grid>

              {/* ─── RIGA 2: cascata categorie, segmento di mercato, toggle ─── */}
              {/* RF-05: cascata car segment → car class → ACRISS (multi-select) */}
              <Mui.Grid item xs={12} sm={6} md={3}>
                <Mui.Autocomplete
                  multiple
                  options={allCarSegments}
                  value={filters.carSegmentFilter}
                  onChange={(_e, v) => setFilters({ ...filters, carSegmentFilter: v })}
                  getOptionLabel={(o) => o.descr}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={(p) => (
                    <Mui.TextField
                      {...p}
                      label={t("availabilityReport.carSegmentLabel", "Car segment")}
                      variant="standard"
                      fullWidth
                    />
                  )}
                />
              </Mui.Grid>
              <Mui.Grid item xs={12} sm={6} md={3}>
                <Mui.Autocomplete
                  multiple
                  options={filteredCarClasses}
                  value={filters.carClassFilter}
                  onChange={(_e, v) => setFilters({ ...filters, carClassFilter: v })}
                  getOptionLabel={(o) => o.descr}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={(p) => (
                    <Mui.TextField
                      {...p}
                      label={t("availabilityReport.carClassLabel", "Car class")}
                      variant="standard"
                      fullWidth
                    />
                  )}
                />
              </Mui.Grid>
              <Mui.Grid item xs={12} sm={6} md={3}>
                <Mui.Autocomplete
                  multiple
                  options={filteredAcriss}
                  value={filters.acrissFilter}
                  onChange={(_e, v) => setFilters({ ...filters, acrissFilter: v })}
                  getOptionLabel={(o) => o.code + (o.label ? ` — ${o.label}` : "")}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={(p) => (
                    <Mui.TextField
                      {...p}
                      label={t("availabilityReport.acrissLabel", "ACRISS")}
                      variant="standard"
                      fullWidth
                    />
                  )}
                />
              </Mui.Grid>

              {/* RF-07: segmento di mercato (multi-select) */}
              <Mui.Grid item xs={12} sm={6} md={3}>
                <Mui.Autocomplete
                  multiple
                  options={allMarketSegments}
                  value={filters.marketSegmentFilter}
                  onChange={(_e, v) => setFilters({ ...filters, marketSegmentFilter: v })}
                  getOptionLabel={(o) => o.descr || o.id}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={(p) => (
                    <Mui.TextField
                      {...p}
                      label={t("availabilityReport.marketSegmentLabel", "Segmento di mercato")}
                      variant="standard"
                      fullWidth
                    />
                  )}
                />
              </Mui.Grid>

              {/* Toggle preesistente only customer at desk */}
              <Mui.Grid item xs={12} md="auto">
                <Mui.Stack spacing={1} alignItems="center" direction="row">
                  <Mui.Typography>
                    {t("availabilityReport.onlyCustomer", "Only customer at desk")}
                  </Mui.Typography>
                  <Mui.Switch
                    checked={filters.onlyCustomer}
                    onChange={(_e, checked) => setFilters({ ...filters, onlyCustomer: checked })}
                  />
                </Mui.Stack>
              </Mui.Grid>
            </Mui.Grid>
          </Mui.Grid>

          <Mui.Grid item xs={12}>
            <Mui.Box sx={{ justifyContent: "end", display: "flex" }}>
              <Mui.Stack direction="row" spacing={2} alignItems="center">
                <Mui.Button
                  variant="outlined"
                  onClick={onClickReset}
                  color={"accent" as any}
                  size="medium"
                >
                  {t("actionButtons.reset")}
                </Mui.Button>
                <Mui.Button
                  variant="contained"
                  onClick={props.onClickProceed}
                  color={"accent" as any}
                  size="medium"
                  // CR LPP-20500: il tasto Cerca è abilitato solo quando location
                  // e range temporale (DAL/AL) sono entrambi valorizzati.
                  disabled={
                    filters.location.length === 0 ||
                    !filters.startDate ||
                    !filters.endDate
                  }
                >
                  {t("actionButtons.search")}
                </Mui.Button>
              </Mui.Stack>
            </Mui.Box>
          </Mui.Grid>
        </Mui.Grid>
      </form>
    </Mui.Paper>
  );
};
